import { Button } from '@/components/ui/button';
import { useBambooTopUpTransactionMutation } from '@/gql/generated';
import DolarIcon from '@/icons/dolar-icon';
import { InvalidInputError } from '@/lib/error-type';
import { handleError } from '@/lib/handle-error';
import { formatDateToTimezone } from '@/lib/timezone';
import { calculateDiscountPercent, formatNumberToCurrency } from '@/lib/utils';
import { FullScreenLoadingContext } from '@/pages/_providers/full-loading-overlay-provider';
import { SoundContext } from '@/pages/(gapsdk)/_provider/sound-provider';
import { AuthenticatedContext } from '@/pages/(gapsdk)/(authenticated)/_provider/authenticated-provider';
import { paymentMethods } from '@/utils/constant';
import bigDecimal from 'js-big-decimal';
import { useContext, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import TopNavigation from '../../../_components/top-navigation';
import CashFailed from './_components/cash-failed';
import CashSuccess from './_components/cash-success';

export default function CashAddFundPage() {
  const navigate = useNavigate();
  const { user } = useContext(AuthenticatedContext);
  const { event: eventLoading } = useContext(FullScreenLoadingContext);
  const { event: eventSound } = useContext(SoundContext);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const price = searchParams.get('price');
  const balanceChange = searchParams.get('balance_change');
  const id = searchParams.get('id');
  const { code } = useParams();

  const discountPercentValue = calculateDiscountPercent(
    Number(balanceChange),
    Number(price),
  );

  const bambooTopUpTransactionMutation = useBambooTopUpTransactionMutation({
    onMutate: () => eventLoading.setIsLoading(true),
    // eslint-disable-next-line @typescript-eslint/no-shadow
    onError: (error) => {
      const errorMessage = handleError({ error });
      if (
        errorMessage instanceof InvalidInputError &&
        errorMessage.errors.length > 0
      ) {
        setError(errorMessage.errors[0]?.message ?? '');
      }
    },
    onSuccess: () => {
      eventLoading.setIsLoading(false);
    },
    onSettled: () => eventLoading.setIsLoading(false),
  });

  const onSubmitPayment = () => {
    bambooTopUpTransactionMutation.mutate({
      input_obj: {
        balance_change: Number(balanceChange) ?? 0,
        date: formatDateToTimezone(
          new Date().toISOString(),
          user?.time_zone!,
          `yyyy-MM-dd'T'HH:mm:ssXXX`,
        ),
        description: '',
        discount_percent_value: Number(
          bigDecimal.multiply(discountPercentValue, 100),
        ),
        gift_card_id: id ?? '',
        whmcs_id: user?.whmcs_id ?? 0,
        payments: [
          {
            payment_method: paymentMethods.CASH,
            price: price ?? '',
          },
        ],
      },
    });
  };

  if (bambooTopUpTransactionMutation.isError) {
    return (
      <CashFailed
        errors={error}
        onBack={() => navigate(`/gift-card/add-fund/${code}?id=${id}`)}
      />
    );
  }

  if (bambooTopUpTransactionMutation.isSuccess) {
    return (
      <CashSuccess
        transaction_id={
          bambooTopUpTransactionMutation.data.bamboo_top_up_transaction.id
        }
      />
    );
  }

  return (
    <div className="flex size-full items-center justify-center">
      <TopNavigation
        onBack={() => navigate(`/gift-card/add-fund/${code}?id=${id}`)}
      />
      <div className="flex flex-col items-center justify-center gap-2">
        <DolarIcon />
        <h5 className="flex-none text-4xl font-bold">Total Amount</h5>
        <div className="flex-none text-9xl font-bold">
          {formatNumberToCurrency(Number(price))}
        </div>
        <Button
          className="my-12 size-full h-[96px] w-full max-w-[448px] bg-primary text-3xl font-bold"
          onClick={() => {
            eventSound.playTap();
            onSubmitPayment();
          }}
        >
          PAY
        </Button>
      </div>
    </div>
  );
}
